import React, {useEffect, useMemo} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import {useGetEmployees} from '../invitationQueries';
import {ReviewInvitationSummaryFragment} from '../../../schema-types/ReviewInvitationSummaryFragment';
import withAutoFormNew, {AutoFormProps} from '../../../components/AutoForm/AutoFormNew';
import AutoField, {
  ActionType,
  SelectField,
  Option,
} from '../../../components/AutoForm/AutoFieldNew';
import {useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../../shared/UPDATES';

interface ConfirmInvitationModalProps {
  open: boolean;
  onClose: () => void;
  review: ReviewInvitationSummaryFragment;
  loading?: boolean;
}

type ConfirmInvitationFormValues = {
  selectedHost: string;
};

const ConfirmInvitationModalComponent: React.FC<
  ConfirmInvitationModalProps & AutoFormProps<ConfirmInvitationFormValues>
> = ({open, onClose, review, loading = false}) => {
  const {employees: allPadStaff, loading: padStaffLoading} = useGetEmployees();
  const {values, submitForm, setValues} = useAutoForm<ConfirmInvitationFormValues>();

  useEffect(() => {
    const initialHost = review.owner?.id || '';
    if (initialHost && !values.selectedHost) {
      setValues(UPDATES._4800, {selectedHost: initialHost});
    }
  }, [review.owner?.id, setValues, values.selectedHost]);

  const staffOptions: Option[] = useMemo(() => {
    return allPadStaff.map((staff) => ({
      label: `${staff.firstName || ''} ${staff.lastName || ''}`.trim(),
      value: staff.id,
    }));
  }, [allPadStaff]);

  const selectHostField: SelectField = {
    id: 'selectedHost',
    label: 'Select Host of Meeting',
    type: ActionType.select,
    initialValue: review.owner?.id || '',
    options: staffOptions,
    size: 'small',
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm Calendly Invitation</DialogTitle>
      <DialogContent>
        <Box sx={{mb: 3}}>
          <Typography variant="body1" sx={{mb: 1}}>
            <strong>Account:</strong> {review.account?.name || 'Unknown Account'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please select who will host the meeting
          </Typography>
        </Box>

        {padStaffLoading ? (
          <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
            <CircularProgress size={20} sx={{mr: 1}} />
            <Typography>Loading staff...</Typography>
          </Box>
        ) : (
          <Box sx={{mb: 2}}>
            <AutoField field={selectHostField} disabled={loading} />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={submitForm}
          color="primary"
          variant="contained"
          disabled={loading || !values.selectedHost}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? 'Sending...' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ConfirmInvitationModal = withAutoFormNew<
  ConfirmInvitationFormValues,
  ConfirmInvitationModalProps
>(ConfirmInvitationModalComponent, undefined, {
  debugPosition: 'bottom',
});
